// import axios
import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    CancelTokenSource,
} from "axios";

import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { loginRequest } from "../../msal/authConfig";
import { getAppInsights, ctService } from './TelemetryService'
import { msalInstance } from "../../index";
import { IAutoHealingSubmitPayload } from "../Interface/Params/IAutoHealingPayload";
import { IAutoHealingReplacement } from "../Interface/View/IAutoHealingView";
import { IReportIssueParam, IScanIssueData } from "../Interface/IIssueCenterInterfaces";

// import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useAccount } from "@azure/msal-react";
// import { PublicClientApplication, EventType } from "@azure/msal-browser";
// import { msalAuth } from '../../msal/MsalAuthProvider'
// const { instance, accounts } = useMsal();
// const userAccount = useAccount(accounts[0]);
// import { msalConfig } from "../../msal/authConfig";
// const msalInstance = new PublicClientApplication(msalConfig);

const NotAvailable = 'NOT_AVAILABLE'

interface AxiosError extends Error {
    config: AxiosRequestConfig
    code?: string
    response?: AxiosResponse
}

export class ApiError extends Error {
    statusCode: string;
    errorMessage: string;
    url: string;
    methodName: string;
    axiosHttpErr: AxiosError
    constructor(axiosHttpErr: AxiosError, ...params) {
        super(...params)

        this.axiosHttpErr = axiosHttpErr
        console.log("ApiError", this.axiosHttpErr)
        this.url =
            (this.axiosHttpErr.config && this.axiosHttpErr.config.url) || NotAvailable

        this.methodName =
            (this.axiosHttpErr.config &&
                this.axiosHttpErr.config.method &&
                this.axiosHttpErr.config.method.toUpperCase()) ||
            NotAvailable

        this.statusCode = this.axiosHttpErr.response ?
            this.axiosHttpErr.response.status.toString()
            : NotAvailable
        this.errorMessage = `Server request failed.`
        const responseError = axiosHttpErr.response?.data
        if(responseError && (responseError.Message || responseError.message)) {
            this.errorMessage = responseError.Message? responseError.Message: responseError.message
        }
    }
}

/**
 * Base loader
 */
class Loader {
    /**
     * Axios instance to make HTTP requests. Configured with user JWT
     */
    axiosHelper: AxiosInstance;
    issueCenterHelper: AxiosInstance;
    chironHelper: AxiosInstance;

    constructor() {
        this.axiosHelper = axios.create({
            //baseURL: "https://msacctwebapi-young.azurewebsites.net",
            //baseURL: "https://sonic-api-ppe.azurewebsites.net",
            // baseURL: "https://msacct-webapi-dev.azurewebsites.net"
            baseURL: `${process.env.REACT_APP_SONIC_API_URL}`,
            timeout: 180000
        });

        this.issueCenterHelper = axios.create({
            baseURL: `${process.env.REACT_APP_ISSUE_CENTER_API_URL}`,
            timeout: 180000
        })

        this.chironHelper = axios.create({
            baseURL:`${process.env.REACT_APP_Chiron_API_URL}`,
            timeout: 180000
        })

        function initHelperConfiguration(helper: AxiosInstance) {
            helper.interceptors.request.use(
                async (config) => {
                    const userAccount = msalInstance.getActiveAccount();
                    if (!userAccount) {
                        throw Error('No active account! Verify a user has been signed in.');
                    }
    
                    const accessTokenRequest = {
                        ...loginRequest,
                        account: userAccount
                    };
    
                    const tokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest)
    
                    // console.log("tokenResponse", tokenResponse)
    
                    config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`
                    return config
                }
            )
    
            helper.interceptors.response.use(
                response => {
                    return response
                },
                error => {
                    try {
                        if (error.response) {
    
                            let apiError = new ApiError(error)
                            ctService.initialize()
                            let appInsight = getAppInsights()
    
                            appInsight.trackException({
                                error: new Error("Chiron API_ERR: " + apiError.url + ', error data:' + apiError.errorMessage),
                                severityLevel: SeverityLevel.Error
                            });
    
                            return Promise.reject(apiError)
                        }
                        else {
                            return Promise.reject(error)
                        }
                    }
                    catch (catchError) {
                        console.log("Sonic api error", catchError)
                        return Promise.reject(error)
                    }
                })
        }
        initHelperConfiguration(this.axiosHelper)
        initHelperConfiguration(this.issueCenterHelper)
        initHelperConfiguration(this.chironHelper)
    }

}

/**
 * AutoHealingService
 */
export class AutoHealingService extends Loader {
    /**
     * get review content
     * @param link 
     * @param fixtype 
     * @returns 
     */
    async getReviewContent(link: string, traceID: string,curationID:string) {
        return (await this.axiosHelper.get(`/api/autohealing/reviewdetails?url=${link}&traceID=${traceID}&curationID=${curationID}`))
            .data as any;
    }

    /**
     * get review content
     * @param link 
     * @param fixtype 
     * @returns 
     */
    async approveReviewContent(approveParams: IAutoHealingSubmitPayload) {
        return await this.axiosHelper.post(`/api/autohealing/confirm`, approveParams);
    }

    /**
     * post replace content
     * @param AutoHealingReplacement 
     * @returns 
     */
    async getReplaceContent(replaceParams: IAutoHealingReplacement) {
        return (await this.axiosHelper.post(`/api/autohealing/applyreplacement`, replaceParams)).data as any;
    }

    /**
     * get review content
     * @param link 
     * @param fixtype 
     * @returns 
     */
    async getHackathonReviewContent(link: string, traceID: string,curationID:string) {
        return (await this.axiosHelper.get(`/api/hackathon/reviewdetails?url=${link}&traceID=${traceID}&curationID=${curationID}`))
            .data as any;
    }

    /**
     * get review content
     * @param link 
     * @param fixtype 
     * @returns 
     */
    async approveHackathonReviewContent(approveParams: IAutoHealingSubmitPayload) {
        return await this.axiosHelper.post(`/api/hackathon/confirm`, approveParams);
    }

    /**
     * post replace content
     * @param AutoHealingReplacement 
     * @returns 
     */
    async getHackathonReplaceContent(replaceParams: IAutoHealingReplacement) {
        return (await this.axiosHelper.post(`/api/hackathon/applyreplacement`, replaceParams)).data as any;
    }
    
    async getContentMarkerReview(link: string, traceID: string, curationID: string, datamart: string) {
        var data = {
            url: link,
            ticketID: traceID,
            curationID: curationID,
            dataMart: datamart
        };
        return (await this.axiosHelper.post(`/api/chiron/reviewdetails`, data))
            .data.Result as any;
    }
    
    async getMakeasReviewed(params: any) {
        return (await this.axiosHelper.post(`/api/chiron/makeasreviewed?curationId=${params.curationId}&ticketGuid=${params.ticketGuid}&dataMart=${params.dataMart}&rollback=${params.rollback}`))
            .data as any;
    }
    
    async getArchiveArticle(params: any) {
        return (await this.axiosHelper.post(`/api/chiron/archivearticle?curationId=${params.curationId}&ticketGuid=${params.ticketGuid}&dataMart=${params.dataMart}`))
            .data as any;
    }
    
    async getImproveArticle(params: any) {
        return (await this.axiosHelper.post(`/api/chiron/improvearticle?curationId=${params.curationId}&ticketGuid=${params.ticketGuid}&dataMart=${params.dataMart}`))
            .data as any;
    }
    
    async getReviewhistory(link: string, traceID: string) {
        return (await this.axiosHelper.get(`/api/chiron/reviewhistory?url=${link}&ticketGuid=${traceID}`))
            .data as any;
    }
    
    async getCorrectOwnerData(traceID: string) {
        return (await this.axiosHelper.get(`/api/chiron/correctownerdata?ticketGuid=${traceID}`))
            .data as any;
    }

    async getCorrectOwner(params: any) {
        return (await this.axiosHelper.post(`/api/chiron/correctowner`, params)).data as any;
    }

    async getAuthandstatus(ticketGuid: any) {
        return (await this.axiosHelper.post(`/api/chiron/authandstatus?ticketGuid=${ticketGuid}`)).data as any;
    }

    async correctPlanningCategory(data: any) {
        return (await this.axiosHelper.post(`/api/chiron/correctplanningcategory`, data)).data as any;
    }
    
    async getReviewHistoryLog(params: any) {
        return (await this.axiosHelper.get(`/api/chiron/reviewhistorylog?ticketGuid=${params.ticketGuid}&url=${params.url}&dataMart=${params.dataMart}`))
            .data as any;
    }
    
    async getPlanningCategoryTree() {
        return (await this.axiosHelper.get(`/api/chiron/planningcategorytree`))
            .data as any;
    }
}

class IssueCenterService extends Loader {
    async getIssueList(articleUrl: string, userEmail: string, sourceData: IScanIssueData[], riskArea: string) {
        const getParam = {
            articleUrl,
            userEmail,
            sourceData,
            riskArea
        }
        const resultData = (await this.issueCenterHelper.post('v2/issue/querybyurl', getParam)).data as any
        return resultData
    }

    async createIssueReport(reportParam: IReportIssueParam) {
        const resultData = (await this.issueCenterHelper.post('v2/issue/create', reportParam)).data as any
        return resultData
    }

    async renewIssueReport(issueId: string, reporter: string, expireTime: string) {
        const renewParam = {
            issueId,
            reporter,
            expireTime
        }
        const resultData = (await this.issueCenterHelper.post('v2/issue/renew', renewParam)).data as any
        return resultData
    }

    async cancelIssueReport(issueId: string, reporter: string) {
        const cancelParam = {
            issueId,
            reporter
        }
        const resultData = (await this.issueCenterHelper.post('v2/issue/cancel', cancelParam)).data as any
        return resultData
    }
}

export default class MsacctAPI {
    constructor() { }

    autoHealingService = new AutoHealingService()
    issueCenterService = new IssueCenterService()
    axiosHelper = new Loader().axiosHelper;
}
